<script>
import { loadStripe } from '@stripe/stripe-js'
import { mapStores } from 'pinia'

import api from '@/cartapi.js'
import CartEventHero from '@/cart/components/CartEventHero.vue'
import OrderSummaryCard from '@/cart/components/OrderSummaryCard.vue'
import { useCartStore } from '@/cart/stores/cart'

let stripe
let stripeElements

export default {
  name: 'CheckoutView',

  components: {
    CartEventHero,
    OrderSummaryCard,
  },

  props: {
    orderToken: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      step: 'contact',
      loading: true,
      submitting: false,
      error: null,
      messages: [],
      countdownTimerId: null,
      localCountdownTimerId: null,
      localSecondsRemaining: 0,
      showExpirationWarning: false,
      contact: {
        name: '',
        email: '',
        phone: '',
        acceptTerms: false,
        allowSms: false,
      },
      // Order data from API
      order: null,
      // Stripe
      paymentIntent: null,
      expressCheckoutElement: null,
      paymentElement: null,
      isComplete: false,
      // Policy modal
      showPolicyModal: false,
      policyModalTitle: '',
      policyModalContent: '',
    }
  },

  computed: {
    ...mapStores(useCartStore),

    // Seconds until hold expires (local countdown)
    countdownSeconds() {
      return this.localSecondsRemaining
    },

    countdownDisplay() {
      const seconds = Math.max(0, this.countdownSeconds)
      const minutes = Math.floor(seconds / 60)
      const remainder = seconds % 60
      const s = remainder.toString().padStart(2, '0')
      return `${minutes}:${s}`
    },

    progressPercent() {
      if (this.step === 'contact') return 50
      if (this.step === 'payment') return 100
      return 0
    },

    currentEvent() {
      return this.cartStore?.selectedEvent
    },

    selectedOffering() {
      return this.cartStore?.selectedOffering
    },

    selectedLocation() {
      return this.cartStore?.selectedLocation || ''
    },

    lockerName() {
      return this.selectedOffering?.displayName || ''
    },

    selectedDates() {
      return this.order?.dates || this.cartStore?.selectedDates || []
    },

    // Total from order (in dollars)
    total() {
      return this.order?.total || 0
    },

    totalCents() {
      return this.order?.totalCents || 0
    },

    subtotal() {
      return this.order?.subtotal || 0
    },

    tax() {
      return this.order?.tax || 0
    },

    orderQuantity() {
      return this.order?.quantity || 1
    },

    canSubmitPayment() {
      return this.isComplete && !this.submitting
    },

    // Policy compliance - ToS URLs
    platformTermsUrl() {
      return 'https://liffhappens.com/terms'
    },
    platformPrivacyUrl() {
      return 'https://liffhappens.com/privacy'
    },
    vendorTermsUrl() {
      return this.order?.vendor_tos_url || this.cartStore?.commSettings?.termsOfService || ''
    },
    vendorPrivacyUrl() {
      return this.cartStore?.commSettings?.privacyPolicy || ''
    },
    vendorName() {
      return this.cartStore?.vendor?.account?.name || 'Event Organizer'
    },
    returnPolicy() {
      return this.cartStore?.commSettings?.returnPolicy || ''
    },
    policySettings() {
      return this.order?.policy_settings || {}
    },
  },

  async created() {
    await this.loadOrder()
    this.startTimer()
  },

  beforeUnmount() {
    if (this.countdownTimerId) {
      window.clearInterval(this.countdownTimerId)
      this.countdownTimerId = null
    }
    if (this.localCountdownTimerId) {
      window.clearInterval(this.localCountdownTimerId)
      this.localCountdownTimerId = null
    }
  },

  methods: {
    isUrl(str) {
      return str && (str.startsWith('http://') || str.startsWith('https://'))
    },

    openPolicyModal(title, content) {
      this.policyModalTitle = title
      this.policyModalContent = content
      this.showPolicyModal = true
    },

    async loadOrder() {
      try {
        this.loading = true
        this.order = await api.orders.get(this.orderToken)

        // Populate cart store with event data if missing (e.g., on page reload)
        if (!this.cartStore.selectedEvent && this.order.event_name) {
          this.cartStore.setSelectedEvent({
            name: this.order.event_name,
            slug: this.order.event_slug,
            backgroundImage: this.order.event_background_image,
            startDate: this.order.event_start_date,
            endDate: this.order.event_end_date,
            locationLabel: this.order.event_location_label,
          })
        }

        if (this.order.isExpired) {
          this.error = 'This order has expired. Please start over.'
          return
        }

        // Default SMS opt-in when event has SMS enabled
        if (this.order.sms_enabled) {
          this.contact.allowSms = true
        }

        if (this.order.status === 'paid') {
          // Already paid, redirect to confirmation
          this.$router.replace({
            name: 'OrderConfirmed',
            params: { orderToken: this.orderToken },
          })
          return
        }
      } catch (err) {
        console.error('Failed to load order', err)
        this.error = 'Unable to load your order. Please try again.'
      } finally {
        this.loading = false
      }
    },

    startTimer() {
      // Initialize local countdown from order
      if (this.order?.secondsRemaining) {
        this.localSecondsRemaining = this.order.secondsRemaining
      }

      // Local countdown: decrement every second
      this.localCountdownTimerId = window.setInterval(() => {
        if (this.localSecondsRemaining > 0) {
          this.localSecondsRemaining--

          // Show warning at 60 seconds
          if (this.localSecondsRemaining === 60) {
            this.showExpirationWarning = true
          }

          // Handle expiration
          if (this.localSecondsRemaining <= 0) {
            this.handleExpiration()
          }
        }
      }, 1000)

      // API sync: refresh order every 30s to verify server state
      this.countdownTimerId = window.setInterval(async () => {
        if (this.order && this.order.status !== 'paid') {
          try {
            this.order = await api.orders.get(this.orderToken)
            // Sync local countdown with server (in case of drift)
            if (this.order.secondsRemaining) {
              this.localSecondsRemaining = this.order.secondsRemaining
            }
            if (this.order.isExpired) {
              this.handleExpiration()
            }
          } catch (err) {
            console.error('Failed to refresh order', err)
          }
        }
      }, 30000)
    },

    handleExpiration() {
      // Clear timers
      if (this.countdownTimerId) {
        window.clearInterval(this.countdownTimerId)
        this.countdownTimerId = null
      }
      if (this.localCountdownTimerId) {
        window.clearInterval(this.localCountdownTimerId)
        this.localCountdownTimerId = null
      }

      // Show toast and redirect to event page
      this.$buefy.toast.open({
        message: 'Your session has expired. Please start over.',
        type: 'is-warning',
        duration: 5000,
      })

      // Redirect to event page
      const eventSlug = this.cartStore.selectedEvent?.slug
      if (eventSlug) {
        this.$router.replace({ name: 'EventLocations', params: { eventSlug } })
      } else {
        this.$router.replace({ name: 'Events' })
      }
    },

    goBack() {
      if (this.step === 'payment') {
        this.step = 'contact'
        return
      }
      this.$router.back()
    },

    validateContact() {
      const { name, email, phone, acceptTerms } = this.contact
      if (!name || !email || !phone || !acceptTerms) {
        this.error = 'Please complete all required fields and accept the terms.'
        return false
      }
      this.error = null
      return true
    },

    async continueToPayment() {
      if (!this.validateContact()) return

      this.submitting = true
      this.error = null

      try {
        // Update order with contact info and ToS acceptance
        this.order = await api.orders.update(this.orderToken, {
          customerName: this.contact.name,
          customerEmail: this.contact.email,
          customerPhone: this.contact.phone,
          tosAccepted: this.contact.acceptTerms,
          smsConsent: this.contact.allowSms,
        })

        // Create payment intent
        this.paymentIntent = await api.orders.createPaymentIntent(this.orderToken)

        this.step = 'payment'

        // Initialize Stripe after DOM updates
        this.$nextTick(() => {
          this.initStripe()
        })
      } catch (err) {
        console.error('Failed to continue to payment', err)
        this.error = err.response?.data?.detail || 'Unable to proceed. Please try again.'
      } finally {
        this.submitting = false
      }
    },

    async initStripe() {
      // Use connected account if vendor has one configured
      const stripeAccount = this.paymentIntent?.stripeAccount
      const stripeOptions = stripeAccount ? { stripeAccount } : {}

      stripe = await loadStripe(import.meta.env.VITE_STRIPE_PUB_KEY, stripeOptions)

      stripeElements = stripe.elements({
        amount: this.totalCents,
        currency: 'usd',
        mode: 'payment',
      })

      this.initExpressCheckout()
      this.initPayment()
    },

    initExpressCheckout() {
      this.expressCheckoutElement = stripeElements.create('expressCheckout', {
        paymentMethods: {
          link: 'never',
        },
      })
      this.expressCheckoutElement.on('confirm', this.submitPayment)
      this.expressCheckoutElement.mount('#express-checkout-element')
    },

    initPayment() {
      this.paymentElement = stripeElements.create('payment', {
        defaultValues: {
          billingDetails: {
            name: this.contact.name,
            email: this.contact.email,
            phone: this.contact.phone,
          },
        },
      })
      this.paymentElement.on('change', (event) => {
        this.isComplete = event.complete
        if (event.error) {
          this.messages = [event.error.message]
        } else {
          this.messages = []
        }
      })
      this.paymentElement.mount('#payment-element')
    },

    editContact() {
      this.step = 'contact'
    },

    handleError(error) {
      if (error.type === 'card_error' || error.type === 'validation_error') {
        this.messages = [error.message]
      } else {
        this.messages = ['An unexpected error occurred.']
      }
      this.submitting = false
    },

    async submitPayment() {
      if (this.submitting) return

      this.submitting = true
      this.error = null
      this.messages = []

      try {
        // Submit elements for validation
        const { error: submitError } = await stripeElements.submit()
        if (submitError) {
          return this.handleError(submitError)
        }

        // Create confirmation token
        const { error, confirmationToken } = await stripe.createConfirmationToken({
          elements: stripeElements,
          params: {
            payment_method_data: {
              billing_details: {
                name: this.contact.name,
                email: this.contact.email,
                phone: this.contact.phone,
              },
            },
            return_url: `${window.location.origin}/order/${this.orderToken}/confirmed`,
          },
        })

        if (error) {
          return this.handleError(error)
        }

        // Send confirmation token to backend
        const result = await api.orders.confirmPayment(this.orderToken, {
          confirmationToken: confirmationToken.id,
        })

        if (result.error) {
          this.error = result.error
          return
        }

        if (result.status === 'requires_action') {
          // Handle 3D Secure or other actions
          const { error: actionError } = await stripe.handleNextAction({
            clientSecret: result.clientSecret,
          })
          if (actionError) {
            this.error = actionError.message
            return
          }
        }

        // Navigate to confirmation
        this.$router.push({
          name: 'OrderConfirmed',
          params: { orderToken: this.orderToken },
        })
      } catch (err) {
        console.error('Payment failed', err)
        this.error = err.response?.data?.detail || 'Payment failed. Please try again.'
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<template>
  <section class="section checkout-view">
    <CartEventHero />
    <div class="container">
      <header class="checkout-header">
        <button type="button" class="button is-text is-small checkout-header__back" @click="goBack">
          <b-icon icon="chevron-left" size="is-small" class="mr-1" />
          <span>Back</span>
        </button>

        <div class="checkout-header__progress">
          <div class="checkout-header__steps">
            <span
              :class="['step-label', { 'is-active': step === 'contact' || step === 'payment' }]"
            >
              Personal Info
            </span>
            <span :class="['step-label', { 'is-active': step === 'payment' }]"> Payment </span>
            <span class="step-label"> Complete </span>
          </div>
          <progress
            class="progress is-primary is-small"
            :value="progressPercent"
            max="100"
          ></progress>
        </div>

        <div class="checkout-header__timer">
          <b-icon icon="clock-outline" size="is-small" class="mr-1" />
          <span>{{ countdownDisplay }}</span>
        </div>
      </header>

      <div v-if="error" class="notification is-danger mb-4">
        {{ error }}
      </div>

      <div v-if="loading" class="has-text-centered py-6">
        <p>Loading your order...</p>
      </div>

      <div v-else-if="!order" class="notification is-warning">
        Order not found. Please start over.
      </div>

      <div v-else class="checkout-columns">
        <!-- Order Summary (mobile: first via CSS order) -->
        <div class="checkout-columns__summary">
          <OrderSummaryCard
            :event-name="currentEvent?.name"
            :location="selectedLocation"
            :locker-name="lockerName"
            :dates="selectedDates"
            :quantity="orderQuantity"
            :subtotal="subtotal"
            :tax="tax"
            :total="total"
            :collapsible="true"
          />
        </div>

        <!-- Main Form Column -->
        <div class="checkout-columns__form">
          <div v-if="step === 'contact'" class="box checkout-panel">
            <h1 class="title is-4">Personal Information</h1>
            <p class="subtitle is-6">
              We will send your locker details to this contact information.
            </p>

            <div class="contact-form">
              <b-field label="Name" message="">
                <b-input
                  v-model="contact.name"
                  placeholder="Arthur Dent"
                  autocomplete="name"
                  required
                />
              </b-field>

              <b-field
                label="Email Address"
                message="Confirmation and access code will be sent here"
              >
                <b-input
                  v-model="contact.email"
                  type="email"
                  placeholder="arthur@example.com"
                  required
                />
              </b-field>

              <b-field label="Phone Number" message="For important updates about your locker">
                <b-input
                  v-model="contact.phone"
                  placeholder="555-555-5555"
                  autocomplete="tel"
                  required
                />
              </b-field>

              <!-- SMS Opt-In Card (only shown when event has SMS enabled) -->
              <div v-if="order?.sms_enabled" class="sms-optin-card mb-4">
                <div class="is-flex is-align-items-center mb-2">
                  <b-icon icon="cellphone-message" size="is-small" class="mr-2 has-text-primary" />
                  <span class="has-text-weight-semibold is-size-7">Text me my locker details</span>
                </div>
                <b-checkbox v-model="contact.allowSms">
                  <span class="is-size-7">
                    Send my locker number and combo code to my phone via SMS.
                  </span>
                </b-checkbox>
                <p class="is-size-7 has-text-grey ml-5 mt-1">
                  Transactional messages only. No marketing. Msg &amp; data rates may apply.
                </p>
              </div>

              <div class="mt-3">
                <!-- Policy Highlight Box (Just-in-Time Disclosure) -->
                <div class="policy-highlight mb-4">
                  <p class="has-text-weight-semibold is-size-7 mb-2">
                    <b-icon icon="alert-circle-outline" size="is-small" class="mr-1" />
                    Important before you book:
                  </p>
                  <ul class="is-size-7">
                    <li v-if="returnPolicy">
                      <strong>Refund Policy:</strong>
                      <a
                        v-if="isUrl(returnPolicy)"
                        :href="returnPolicy"
                        target="_blank"
                        rel="noopener"
                        >View Refund Policy</a
                      >
                      <span v-else>{{ returnPolicy }}</span>
                    </li>
                    <li v-else><strong>No Refunds:</strong> All sales are final.</li>
                    <li>
                      <strong>Use at Your Own Risk:</strong> The vendor is not liable for theft,
                      water damage, or loss of items stored.
                    </li>
                    <li v-if="policySettings.age_restriction">
                      <strong>Age Restriction:</strong> {{ policySettings.age_restriction }}
                    </li>
                  </ul>
                </div>

                <!-- ToS Acceptance with Links (or Modal triggers for text content) -->
                <b-field>
                  <b-checkbox v-model="contact.acceptTerms">
                    <span class="is-size-7">
                      I agree to the
                      <a :href="platformTermsUrl" target="_blank" rel="noopener">Platform Terms</a>,
                      <a :href="platformPrivacyUrl" target="_blank" rel="noopener">Privacy Policy</a
                      ><span v-if="vendorTermsUrl"
                        >, and
                        <a
                          v-if="isUrl(vendorTermsUrl)"
                          :href="vendorTermsUrl"
                          target="_blank"
                          rel="noopener"
                          >{{ vendorName }}'s Terms</a
                        >
                        <a
                          v-else
                          href="#"
                          @click.prevent="openPolicyModal(vendorName + '\'s Terms', vendorTermsUrl)"
                          >{{ vendorName }}'s Terms</a
                        ></span
                      ><span v-if="vendorPrivacyUrl"
                        >, and
                        <a
                          v-if="isUrl(vendorPrivacyUrl)"
                          :href="vendorPrivacyUrl"
                          target="_blank"
                          rel="noopener"
                          >{{ vendorName }}'s Privacy Policy</a
                        >
                        <a
                          v-else
                          href="#"
                          @click.prevent="openPolicyModal('Privacy Policy', vendorPrivacyUrl)"
                          >{{ vendorName }}'s Privacy Policy</a
                        ></span
                      >.
                    </span>
                  </b-checkbox>
                </b-field>
              </div>
            </div>

            <div class="checkout-actions">
              <b-button type="is-primary" expanded :loading="submitting" @click="continueToPayment">
                <span>Continue to Payment</span>
                <b-icon icon="arrow-right" size="is-small" class="ml-2" />
              </b-button>
            </div>
          </div>

          <div v-else class="checkout-panel">
            <h1 class="title is-4">Secure Payment</h1>
            <p class="subtitle is-6">
              We will send your locker details to this contact information.
            </p>

            <div class="box mb-4">
              <div class="level is-mobile mb-2">
                <div class="level-left">
                  <p class="is-size-7 has-text-grey">Contact Details</p>
                </div>
                <div class="level-right">
                  <button type="button" class="button is-text is-small" @click="editContact">
                    <b-icon icon="pencil" size="is-small" class="mr-1" />
                    <span>Edit</span>
                  </button>
                </div>
              </div>
              <p class="is-size-7 mb-1">
                {{ contact.email || 'email@example.com' }}
              </p>
              <p class="is-size-7 mb-1">
                {{ contact.name || 'Name' }}
              </p>
              <p class="is-size-7">
                {{ contact.phone || 'Phone number' }}
              </p>
            </div>

            <div class="box">
              <p class="is-size-6 has-text-weight-semibold mb-3">Payment Information</p>

              <!-- Express Checkout (Apple Pay, Google Pay) -->
              <div v-show="!submitting" id="express-checkout-element" class="mb-4"></div>

              <!-- Payment Element (Card, etc.) -->
              <form id="payment-form" @submit.prevent="submitPayment">
                <p class="is-size-7 has-text-success mb-3">
                  <b-icon icon="lock" size="is-small" class="mr-1" />
                  Secure checkout powered by Stripe
                </p>

                <div id="payment-element" class="payment-element-container"></div>

                <p v-for="msg in messages" :key="msg" class="help is-danger mt-2">
                  {{ msg }}
                </p>

                <div class="checkout-actions mt-4">
                  <b-button
                    type="is-primary"
                    expanded
                    native-type="submit"
                    :disabled="!canSubmitPayment"
                    :loading="submitting"
                  >
                    <span>Pay ${{ total.toFixed(2) }}</span>
                  </b-button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <!-- Sticky Footer (mobile only) -->
      <div v-if="order && step === 'contact'" class="sticky-footer is-hidden-tablet">
        <div class="sticky-footer__content">
          <div class="sticky-footer__summary">
            <span class="sticky-footer__total">${{ total.toFixed(2) }}</span>
            <span class="sticky-footer__label"
              >{{ orderQuantity }}× {{ lockerName || 'Locker' }}</span
            >
          </div>
          <b-button
            type="is-primary"
            :disabled="!contact.name || !contact.email || !contact.phone || !contact.acceptTerms"
            :loading="submitting"
            @click="continueToPayment"
          >
            Continue
            <b-icon icon="arrow-right" size="is-small" class="ml-1" />
          </b-button>
        </div>
      </div>
    </div>

    <!-- Expiration Warning Modal -->
    <b-modal v-model="showExpirationWarning" :can-cancel="false" has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head has-background-warning-light">
          <p class="modal-card-title">
            <b-icon icon="clock-alert-outline" class="mr-2" />
            Time Running Out!
          </p>
        </header>
        <section class="modal-card-body">
          <p class="mb-3">
            You have <strong>{{ countdownDisplay }}</strong> remaining to complete your purchase.
          </p>
          <p class="has-text-grey">
            Your locker hold will expire soon. Please complete payment to secure your reservation.
          </p>
        </section>
        <footer class="modal-card-foot">
          <b-button type="is-primary" @click="showExpirationWarning = false">
            Continue Checkout
          </b-button>
        </footer>
      </div>
    </b-modal>

    <!-- Policy Content Modal -->
    <b-modal v-model="showPolicyModal" has-modal-card scroll="keep">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ policyModalTitle }}</p>
          <button class="delete" aria-label="close" @click="showPolicyModal = false" />
        </header>
        <section class="modal-card-body">
          <div class="content" style="white-space: pre-wrap">{{ policyModalContent }}</div>
        </section>
        <footer class="modal-card-foot">
          <b-button @click="showPolicyModal = false">Close</b-button>
        </footer>
      </div>
    </b-modal>
  </section>
</template>

<style scoped>
.checkout-view {
  padding-top: 1.5rem;
}

.checkout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
}

.checkout-header__back {
  border: none;
  padding-left: 0;
}

.checkout-header__progress {
  flex: 1;
  margin: 0 1rem;
}

.checkout-header__steps {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
}

.checkout-header__timer {
  display: flex;
  align-items: center;
}

.step-label {
  font-size: 0.75rem;
  color: #9ca3af;
}

.step-label.is-active {
  color: #111827;
  font-weight: 600;
}

.checkout-panel {
  border-radius: 12px;
}

.contact-form {
  margin-top: 1rem;
}

/* Policy Highlight Box (Just-in-Time Disclosure) */
.policy-highlight {
  background-color: #fffbeb; /* Light yellow warning background */
  border-left: 4px solid #f59e0b;
  padding: 0.75rem 1rem;
  border-radius: 4px;
}

.policy-highlight ul {
  margin: 0;
  padding-left: 1.25rem;
  color: #78350f;
}

.policy-highlight li {
  margin-bottom: 0.25rem;
}

.policy-highlight a {
  color: #0066cc;
  text-decoration: none;
}

.policy-highlight a:hover {
  text-decoration: underline;
}

.sms-optin-card {
  background: #f0f9ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}

.checkout-actions {
  margin-top: 1.5rem;
}

.summary-card {
  border-radius: 12px;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
}

.summary-line--total {
  font-weight: 600;
}

.summary-line .label {
  color: #6b7280;
}

.payment-element-container {
  min-height: 200px;
}

#express-checkout-element:empty {
  display: none;
}

@media (min-width: 769px) {
  .checkout-view {
    padding-top: 2rem;
  }
}

/* Checkout Columns - Mobile first (summary at top) */
.checkout-columns {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.checkout-columns__summary {
  order: -1; /* Show first on mobile */
}

.checkout-columns__form {
  order: 0;
}

@media (min-width: 769px) {
  .checkout-columns {
    flex-direction: row;
  }

  .checkout-columns__form {
    flex: 2;
    order: 0;
  }

  .checkout-columns__summary {
    flex: 1;
    order: 1; /* Show after form on desktop */
  }
}

/* Sticky Footer */
.sticky-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #e5e7eb;
  padding: 0.75rem 1rem;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.sticky-footer__content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 600px;
  margin: 0 auto;
}

.sticky-footer__summary {
  display: flex;
  flex-direction: column;
}

.sticky-footer__total {
  font-size: 1.1rem;
  font-weight: 600;
}

.sticky-footer__label {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Add bottom padding for sticky footer */
@media (max-width: 768px) {
  .checkout-view {
    padding-bottom: 5rem;
  }
}
</style>
